<template>
  <div class="row mt-1 ml-1" style="line-height: normal; column-gap: 8px; row-gap: 4px">
    <div
      class="d-flex"
      v-for="flugverfuegbarkeit in latestFVCRequests"
      :key="'flugverfuegbarkeit-' + flugverfuegbarkeit.id"
      :id="'flugverfuegbarkeit-' + flugverfuegbarkeit.id"
    >
      <div class="flugverfuegbarkeit-badge" :class="istBgColor(flugverfuegbarkeit)">
        <div class="flugverfuegbarkeit-status">
          {{ statusLabel(flugverfuegbarkeit) }}
        </div>
        <div class="flugverfuegbarkeit-flughafen">
          {{ flugverfuegbarkeit.deutscherAbflughafenIataCode }}
        </div>
      </div>
      <b-tooltip
        :target="'flugverfuegbarkeit-' + flugverfuegbarkeit.id"
        @show="ladeLetzteBuchungsantworten(flugverfuegbarkeit)"
        custom-class="custom-tooltip-fvc-widget"
        placement="left"
      >
        <br />
        <b-spinner v-if="isLoadingLetzteBuchungsantworten"></b-spinner>
        <div v-else-if="letzteBuchungsantworten.length > 0">
          <div class="mb-2">
            Abfragealter:
            <b>{{ daysSince(flugverfuegbarkeit.abfragedatum) }}</b>
          </div>
          Letzte Buchungsantworten:
          <LineChart
            style="width: 300px; height: 200px; margin: 0; padding: 0"
            :datapoints="letzteBuchungsantworten"
            :showLegend="false"
          ></LineChart>
        </div>
      </b-tooltip>
    </div>
  </div>
</template>
<script>
import { differenceInDays, parseISO, isAfter, isToday, format } from 'date-fns';
import * as odataService from '@/core/common/services/odata.service';
import LineChart from '@/components/flugverfuegbarkeit/line-chart.vue';

export default {
  props: {
    flugverfuegbarkeiten: { type: Array },
    data: { type: Object },
    excludeAirports: { type: Array, default: () => [], required: false },
  },
  components: { LineChart },

  data() {
    return {
      letzteBuchungsantworten: [],
      isLoadingLetzteBuchungsantworten: false,
    };
  },
  computed: {
    latestFVCRequests() {
      const newestEntries = new Map();

      // Iterate through the data
      for (const entry of this.flugverfuegbarkeiten) {
        const iataCode = entry.deutscherAbflughafenIataCode;
        const entryDate = parseISO(entry.abfragedatum);

        // Check if iataCode is already in the Map
        if (!newestEntries.has(iataCode)) {
          newestEntries.set(iataCode, entry);
        } else {
          // Compare dates to find the newest entry
          const currentNewestEntry = newestEntries.get(iataCode);
          if (isAfter(entryDate, parseISO(currentNewestEntry.abfragedatum))) {
            newestEntries.set(iataCode, entry);
          }
        }
      }
      const newestEntriesList = Array.from(newestEntries.values());

      if (this.flugverfuegbarkeiten) {
        const sortOrder = ['FRA', 'MUC', 'DUS', 'BER', 'HAM', 'VIE', 'ZRH'];
        let fvcData = newestEntriesList;

        if (this.excludeAirports && this.excludeAirports.length > 0) {
          fvcData = fvcData.filter(item => !this.excludeAirports.includes(item.deutscherAbflughafenIataCode));
        }

        return fvcData.sort((a, b) => {
          const codeA = sortOrder.indexOf(a.deutscherAbflughafenIataCode);
          const codeB = sortOrder.indexOf(b.deutscherAbflughafenIataCode);

          return codeA - codeB;
        });
      } else return null;
    },
  },
  methods: {
    ladeLetzteBuchungsantworten(flugverfuegbarkeit) {
      this.letzteBuchungsantworten = [];
      this.isLoadingLetzteBuchungsantworten = true;
      const {
        deutscherAbflughafenIataCode,
        abfragedatum,
        zielflughafenIataCode,
        abflughafenIataCode,
        zimmertyp,
        verpflegung,
      } = flugverfuegbarkeit;
      const filterString = `reisetermin/reiseterminkuerzel eq '${this.data.reiseterminkuerzel}' and deutscherAbflughafenIataCode eq '${deutscherAbflughafenIataCode}' and abfragedatum le ${abfragedatum} and zielflughafenIataCode eq '${zielflughafenIataCode}' and abflughafenIataCode eq '${abflughafenIataCode}' and zimmertyp eq '${zimmertyp}' and verpflegung eq '${verpflegung}'`;
      odataService
        .getFlugverfuegbarkeit({
          filter: filterString,
          top: 5,
          expand: { reisetermin: { expand: { reise: {} } } },
          orderBy: 'abfragedatum desc',
        })
        .then(response => {
          this.letzteBuchungsantworten = response.data.map(item => ({
            ...item,
            abfragedatum: format(parseISO(item.abfragedatum), 'dd.MM.yyyy'),
          }));
          this.isLoadingLetzteBuchungsantworten = false;
          console.log(this.letzteBuchungsantworten);
        })
        .catch(_ => (this.isLoadingLetzteBuchungsantworten = false));
    },
    istBgColor(flugverfuegbarkeit) {
      if (flugverfuegbarkeit.status === 'NichtVerfuegbar' || flugverfuegbarkeit.aufpreis > 200) return 'red';
      else if (flugverfuegbarkeit.status === 'Verfuegbar') return 'green';
      else if (flugverfuegbarkeit.status === 'AufAnfrage') return 'gray';
    },
    statusLabel(flugverfuegbarkeit) {
      return flugverfuegbarkeit.aufpreis !== null
        ? flugverfuegbarkeit.aufpreis === 0
          ? 'J'
          : flugverfuegbarkeit.aufpreis + '€'
        : flugverfuegbarkeit.status === 'AufAnfrage'
        ? 'RQ'
        : 'N';
    },
    daysSince(dateString) {
      const inputDate = parseISO(dateString);
      const currentDate = new Date();

      // If the input date is today, the difference will be 0
      if (isToday(inputDate)) {
        return '0 Tage';
      }

      const daysDifference = differenceInDays(currentDate, inputDate);
      return daysDifference === 1 ? '1 Tag' : daysDifference + ' Tage';
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/sass/components/mixins/_kachel-badge.scss';

.flugverfuegbarkeit-badge {
  @include kachel-badge-base;
  flex-direction: column;
  padding: 4px;
  width: 40px;

  @include kachel-badge-colors;
}
.flugverfuegbarkeit-status {
  font-weight: 700;
  font-size: 12px;
  line-height: 12px;
}
.flugverfuegbarkeit-flughafen {
  font-weight: 400;
  font-size: 9px;
  line-height: 11px;
}
.tooltip {
  opacity: 1 !important;
}
</style>
<style>
.custom-tooltip-fvc-widget > .tooltip-inner {
  max-width: none;
  width: 500;
  font-size: 14px;
}
</style>
